/**
 * Peer Groups Hooks - React Query hooks for SIP Peer Groups
 * 
 * Uses Sip.PeerGroupInfo types from @sbclite/core for type safety.
 * Following SST Console pattern from CORE_SST_PATTERN_IMPLEMENTATION.md
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { Sip } from '@sbclite/core';
import type { SipPeerInfo, SipPeersResponse } from '../types/sip';

/**
 * Peer group info with embedded peer members
 */
export interface PeerGroupWithPeers extends Sip.PeerGroupInfo {
  peerCount: number;
  peers: PeerMemberInfo[];
}

/**
 * Peer member info within a group
 */
export interface PeerMemberInfo {
  id: string;
  name: string;
  type: string;
  enabled: boolean;
  groupWeight: number | null;
  groupPriority: number | null;
  groupEnabled: boolean | null;
}

/**
 * API Response types
 */
interface PeerGroupsResponse {
  success: boolean;
  data: PeerGroupWithPeers[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface PeerGroupResponse {
  success: boolean;
  data: PeerGroupWithPeers;
}

interface PeerGroupMutationData {
  name?: string;
  description?: string;
  enabled?: boolean;
  groupType?: 'peer_group' | 'trunk_group' | 'carrier_group' | 'teams_group' | 'emergency_group';
  selectionMode?: 'failover' | 'parallel';
  loadBalancingStrategy?: 'round_robin' | 'weighted' | 'least_calls';
  maxConcurrentCalls?: number;
  healthCheckEnabled?: boolean;
  minActiveMembers?: number | null;
  maxCallsPerPeer?: number | null;
  hotSwapMode?: boolean | null;
  sessionAffinity?: boolean | null;
  affinityTimeoutSeconds?: number | null;
  autoFailover?: boolean | null;
  failoverRetries?: number | null;
  failoverDelayMs?: number | null;
  backupGroupId?: string | null;
  mediaRealmId?: string | null;
  preferredSipInterfaceId?: string | null;
  tlsContextName?: string | null;
  customHeaderLines?: string[];
  totalCalls?: number | null;
  activeCalls?: number | null;
  failedCalls?: number | null;
  lastUsedAt?: Date | null;
  averageCallDurationSeconds?: number | null;
  tags?: string[];
  environment?: string | null;
  carrierId?: string | null;
  carrierContractId?: string | null;
  carrierSupportContact?: string | null;
  teamsTenantId?: string | null;
  teamsDirectRoutingDomains?: string[];
}

/**
 * Fetch all peer groups with their members
 */
export function usePeerGroups() {
  return useQuery({
    queryKey: ['peer-groups'],
    queryFn: async (): Promise<PeerGroupsResponse> => {
      const response = await api.get<PeerGroupsResponse>('/api/peer-groups');
      return response.data as PeerGroupsResponse;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Fetch peer groups formatted for dropdown selection
 */
export function usePeerGroupsForDropdown() {
  return useQuery({
    queryKey: ['peer-groups-dropdown'],
    queryFn: async () => {
      const response = await api.get<PeerGroupsResponse>('/api/peer-groups');
      const groups = response.data?.data || [];
      return groups
        .filter((group) => group.enabled)
        .map((group) => ({
          value: group.id,
          label: group.name,
          description: group.description,
          groupType: group.groupType,
          peerCount: group.peerCount,
        }));
    },
    staleTime: 60000,
  });
}

/**
 * Fetch single peer group by ID with its members
 */
export function usePeerGroup(groupId: string | null | undefined) {
  return useQuery<PeerGroupResponse | null>({
    queryKey: ['peer-group', groupId],
    queryFn: async () => {
      if (!groupId) return null;
      const response = await api.get<PeerGroupResponse>(`/api/peer-groups/${groupId}`);
      return response.data as PeerGroupResponse;
    },
    enabled: !!groupId,
  });
}

/**
 * Fetch peers that are not assigned to any group (available for assignment)
 */
export function useAvailablePeers() {
  return useQuery({
    queryKey: ['available-peers'],
    queryFn: async () => {
      const response = await api.get<SipPeersResponse>('/api/sip-peers');
      const peers = response.data?.data || [];
      // Filter peers that don't have a groupId
      return peers.filter((peer: SipPeerInfo) => !peer.groupId);
    },
    staleTime: 30000,
  });
}

/**
 * Create peer group mutation
 */
export function useCreatePeerGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PeerGroupMutationData & { name: string }) => {
      const response = await api.post<PeerGroupResponse>('/api/peer-groups', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
      queryClient.invalidateQueries({ queryKey: ['peer-groups-dropdown'] });
    },
  });
}

/**
 * Update peer group mutation
 */
export function useUpdatePeerGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, data }: {
      groupId: string;
      data: {
        description?: string;
      } & PeerGroupMutationData;
    }) => {
      const response = await api.put<PeerGroupResponse>(`/api/peer-groups/${groupId}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
      queryClient.invalidateQueries({ queryKey: ['peer-group', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['peer-groups-dropdown'] });
    },
  });
}

/**
 * Delete peer group mutation
 */
export function useDeletePeerGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      await api.delete(`/api/peer-groups/${groupId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
      queryClient.invalidateQueries({ queryKey: ['peer-groups-dropdown'] });
      queryClient.invalidateQueries({ queryKey: ['available-peers'] });
    },
  });
}

/**
 * Toggle peer group enabled/disabled
 */
export function useTogglePeerGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, enabled }: { groupId: string; enabled: boolean }) => {
      const response = await api.put<PeerGroupResponse>(`/api/peer-groups/${groupId}`, { enabled });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
      queryClient.invalidateQueries({ queryKey: ['peer-group', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['peer-groups-dropdown'] });
    },
  });
}

/**
 * Add peer to group (updates peer's groupId)
 */
export function useAddPeerToGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ peerId, groupId, weight, priority }: {
      peerId: string;
      groupId: string;
      weight?: number;
      priority?: number;
    }) => {
      await api.post(`/api/sip-peers/${peerId}/assign-group`, {
        groupId,
        groupWeight: weight ?? 50,
        groupPriority: priority ?? 1,
        groupEnabled: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
      queryClient.invalidateQueries({ queryKey: ['peer-group'] });
      queryClient.invalidateQueries({ queryKey: ['available-peers'] });
      queryClient.invalidateQueries({ queryKey: ['sip-peers'] });
    },
  });
}

/**
 * Remove peer from group (sets peer's groupId to null)
 */
export function useRemovePeerFromGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (peerId: string) => {
      await api.post(`/api/sip-peers/${peerId}/unassign-group`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
      queryClient.invalidateQueries({ queryKey: ['peer-group'] });
      queryClient.invalidateQueries({ queryKey: ['available-peers'] });
      queryClient.invalidateQueries({ queryKey: ['sip-peers'] });
    },
  });
}

/**
 * Update peer membership in group (weight, priority, enabled)
 */
export function useUpdatePeerMembership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ peerId, weight, priority, enabled }: {
      peerId: string;
      weight?: number;
      priority?: number;
      enabled?: boolean;
    }) => {
      const updates: Record<string, unknown> = {};
      if (weight !== undefined) updates.groupWeight = weight;
      if (priority !== undefined) updates.groupPriority = priority;
      if (enabled !== undefined) updates.groupEnabled = enabled;
      
      await api.put(`/api/sip-peers/${peerId}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peer-groups'] });
      queryClient.invalidateQueries({ queryKey: ['peer-group'] });
      queryClient.invalidateQueries({ queryKey: ['sip-peers'] });
    },
  });
}
